// src/pages/AdminApps.tsx

import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  APP_STATUS_LABELS,
  CREDIT_PERIOD_LABELS,
  fetchAppsCatalog,
  fetchAppStatuses,
  updateAppStatus,
  updateApp,
  createApp,
  deleteApp,
  type AppInfo,
  type AppStatus,
  type CreditPeriod,
} from '../utils/appCatalog';

const AdminApps: React.FC = () => {
  const { user, token } = useAuth();
  const [apps, setApps] = useState<AppInfo[]>([]);
  const [appStatuses, setAppStatuses] = useState<Record<string, AppStatus>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingApp, setEditingApp] = useState<AppInfo | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    appUrl: '',
    status: 'active' as AppStatus,
    creditCost: 0,
    creditPeriod: 'month' as CreditPeriod,
  });
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const [newAppName, setNewAppName] = useState('');
  const [newAppUrl, setNewAppUrl] = useState('');
  const [newAppStatus, setNewAppStatus] = useState<AppStatus>('planned');
  const [newAppCreditCost, setNewAppCreditCost] = useState('0');
  const [newAppCreditPeriod, setNewAppCreditPeriod] = useState<CreditPeriod>('month');
  const [newAppError, setNewAppError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      if (!token) return;

      setLoading(true);
      const [catalogData, statusesData] = await Promise.all([
        fetchAppsCatalog(token),
        fetchAppStatuses(token),
      ]);

      setApps(catalogData);
      setAppStatuses(statusesData);
      setLoading(false);
    };

    loadData();
  }, [token]);

  const handleCreateApp = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) return;
    setNewAppError('');
    setSaving(true);
    try {
      const newApp = await createApp(token, {
        name: newAppName,
        appUrl: newAppUrl,
        status: newAppStatus,
        creditCost: parseInt(newAppCreditCost, 10) || 0,
        creditPeriod: newAppCreditPeriod,
      });
      if (newApp) {
        setApps([...apps, newApp]);
        setAppStatuses({ ...appStatuses, [newApp.id]: newAppStatus });
        setNewAppName('');
        setNewAppUrl('');
        setNewAppStatus('planned');
        setNewAppCreditCost('0');
        setNewAppCreditPeriod('month');
      }
    } catch (err: any) {
      setNewAppError(err.message || 'Failed to create app');
    } finally {
      setSaving(false);
    }
  };

  const handleEditOpen = (app: AppInfo) => {
    setEditingApp(app);
    setEditForm({
      name: app.name,
      description: app.description || '',
      appUrl: app.appUrl,
      status: (app.status as AppStatus) || 'active',
      creditCost: app.creditCost ?? 0,
      creditPeriod: app.creditPeriod ?? 'month',
    });
    setEditError('');
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !editingApp) return;
    setEditError('');
    setEditSaving(true);
    const updated = await updateApp(token, editingApp.id, editForm);
    if (updated) {
      setApps(apps.map(a => a.id === editingApp.id ? updated : a));
      setAppStatuses({ ...appStatuses, [updated.id]: updated.status as AppStatus });
      setEditingApp(null);
    } else {
      setEditError('Failed to save changes. Please try again.');
    }
    setEditSaving(false);
  };

  const handleDeleteApp = async (appId: string, appName: string) => {
    if (!token) return;
    if (!window.confirm(`Delete "${appName}"? This cannot be undone.`)) return;
    setDeletingId(appId);
    const success = await deleteApp(token, appId);
    if (success) {
      setApps(apps.filter(a => a.id !== appId));
      const next = { ...appStatuses };
      delete next[appId];
      setAppStatuses(next);
    }
    setDeletingId(null);
  };

  const handleStatusChange = async (appId: string, status: AppStatus) => {
    if (!token) return;

    const prevStatuses = { ...appStatuses };
    setAppStatuses({ ...appStatuses, [appId]: status });

    const success = await updateAppStatus(token, appId, status);
    if (!success) {
      setAppStatuses(prevStatuses);
    }
  };

  if (loading) {
    return (
      <div className="section container" style={{ textAlign: 'center', padding: '4rem' }}>
        <p>Loading apps...</p>
      </div>
    );
  }

  return (
    <>
    <div className="section container">
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div>
          <h1 style={{ marginBottom: '0.25rem' }}>App Management</h1>
          <p className="text-secondary">Signed in as {user?.email}</p>
        </div>

        {/* Add New App */}
        <div style={{ marginTop: '2rem' }}>
          <h2 style={{ marginBottom: '1rem' }}>Add New App</h2>
          <div className="card" style={{ padding: '1.5rem' }}>
            <form onSubmit={handleCreateApp} style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>App Name</label>
                <input
                  type="text"
                  value={newAppName}
                  onChange={(e) => setNewAppName(e.target.value)}
                  required
                  placeholder="e.g. My New App"
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div style={{ flex: '2 1 280px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>App URL</label>
                <input
                  type="url"
                  value={newAppUrl}
                  onChange={(e) => setNewAppUrl(e.target.value)}
                  required
                  placeholder="https://app.snowbirdindustries.us"
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div style={{ flex: '0 1 160px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Status</label>
                <select
                  value={newAppStatus}
                  onChange={(e) => setNewAppStatus(e.target.value as AppStatus)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                >
                  {Object.entries(APP_STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: '0 1 120px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Credits</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={newAppCreditCost}
                  onChange={(e) => setNewAppCreditCost(e.target.value)}
                  placeholder="0"
                  title="0 = free"
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div style={{ flex: '0 1 140px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Period</label>
                <select
                  value={newAppCreditPeriod}
                  onChange={(e) => setNewAppCreditPeriod(e.target.value as CreditPeriod)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                >
                  {Object.entries(CREDIT_PERIOD_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <button type="submit" className="btn btn-primary" disabled={saving} style={{ whiteSpace: 'nowrap' }}>
                  {saving ? 'Adding...' : 'Add App'}
                </button>
              </div>
            </form>
            {newAppError && <p style={{ color: '#dc2626', marginTop: '0.75rem', fontSize: '0.875rem' }}>{newAppError}</p>}
          </div>
        </div>

        {/* App Status Table */}
        <div style={{ marginTop: '2rem' }}>
          <h2 style={{ marginBottom: '1rem' }}>App Status</h2>
          <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>App</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>URL</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Credits</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Visibility</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}></th>
                </tr>
              </thead>
              <tbody>
                {apps.map((app) => {
                  const status = appStatuses[app.id] || app.status || 'active';
                  const isActive = status === 'active';
                  const isBeta = status === 'beta';
                  let visibilityLabel: string;
                  let visibilityColor: string;
                  let visibilityBg: string;

                  if (isActive) {
                    visibilityLabel = 'Visible to All';
                    visibilityColor = '#166534';
                    visibilityBg = '#dcfce7';
                  } else if (isBeta) {
                    visibilityLabel = 'Employees + Admins';
                    visibilityColor = '#92400e';
                    visibilityBg = '#fef3c7';
                  } else {
                    visibilityLabel = 'Admin Only';
                    visibilityColor = '#6b7280';
                    visibilityBg = '#f3f4f6';
                  }

                  return (
                    <tr key={app.id}>
                      <td style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                        <strong>{app.name}</strong>
                      </td>
                      <td style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                        <a
                          href={app.appUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-secondary"
                          style={{ fontSize: '0.875rem' }}
                        >
                          {app.appUrl.replace('https://', '')}
                        </a>
                      </td>
                      <td style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                        <select
                          value={status}
                          onChange={(e) => handleStatusChange(app.id, e.target.value as AppStatus)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            backgroundColor: 'white',
                            minWidth: '150px',
                          }}
                        >
                          {Object.entries(APP_STATUS_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                        {app.creditCost > 0 ? (
                          <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                            <strong>{app.creditCost}</strong> {CREDIT_PERIOD_LABELS[app.creditPeriod]?.toLowerCase()}
                          </span>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>Free</span>
                        )}
                      </td>
                      <td style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            backgroundColor: visibilityBg,
                            color: visibilityColor,
                          }}
                        >
                          {visibilityLabel}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => handleEditOpen(app)}
                            style={{
                              padding: '0.4rem 0.75rem',
                              borderRadius: '6px',
                              border: '1px solid #93c5fd',
                              backgroundColor: '#fff',
                              color: '#1d4ed8',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              fontWeight: '500',
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteApp(app.id, app.name)}
                            disabled={deletingId === app.id}
                            style={{
                              padding: '0.4rem 0.75rem',
                              borderRadius: '6px',
                              border: '1px solid #fca5a5',
                              backgroundColor: deletingId === app.id ? '#f3f4f6' : '#fff',
                              color: '#dc2626',
                              cursor: deletingId === app.id ? 'not-allowed' : 'pointer',
                              fontSize: '0.8rem',
                              fontWeight: '500',
                            }}
                          >
                            {deletingId === app.id ? 'Deleting…' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-secondary" style={{ fontSize: '0.875rem', marginTop: '0.75rem' }}>
            Active apps are visible to all users. Beta apps are visible to employees and admins. All other statuses are admin-only.
          </p>
        </div>
      </div>
    </div>

    {/* Edit Modal */}
    {editingApp && (
      <div
        style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}
        onClick={(e) => { if (e.target === e.currentTarget) setEditingApp(null); }}
      >
        <div className="card" style={{ width: '100%', maxWidth: '520px', padding: '2rem', margin: '1rem' }}>
          <h2 style={{ marginBottom: '1.5rem' }}>Edit App</h2>
          <form onSubmit={handleEditSave} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '500' }}>App Name</label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
                style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '500' }}>Description</label>
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={3}
                style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', boxSizing: 'border-box', resize: 'vertical' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '500' }}>App URL</label>
              <input
                type="url"
                value={editForm.appUrl}
                onChange={(e) => setEditForm({ ...editForm, appUrl: e.target.value })}
                required
                style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '500' }}>Status</label>
              <select
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value as AppStatus })}
                style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
              >
                {Object.entries(APP_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <div style={{ flex: '1 1 120px' }}>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '500' }}>Credits</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={editForm.creditCost}
                  onChange={(e) => setEditForm({ ...editForm, creditCost: parseInt(e.target.value, 10) || 0 })}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', boxSizing: 'border-box' }}
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>0 = free</p>
              </div>
              <div style={{ flex: '1 1 160px' }}>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '500' }}>Billing Period</label>
                <select
                  value={editForm.creditPeriod}
                  onChange={(e) => setEditForm({ ...editForm, creditPeriod: e.target.value as CreditPeriod })}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                >
                  {Object.entries(CREDIT_PERIOD_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
            {editError && <p style={{ color: '#dc2626', fontSize: '0.875rem', margin: 0 }}>{editError}</p>}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <button
                type="button"
                onClick={() => setEditingApp(null)}
                style={{ padding: '0.6rem 1.25rem', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: '#fff', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </>
  );
};

export default AdminApps;
